import React, { useContext, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Modal, Space, Switch, Tooltip, message } from 'antd';
import { useLocation } from 'react-router-dom';
import { LAUNCH_FEATURES } from '../../../../constants/define/translation';
import { LaunchFeaturesType } from '../../../../types/launchFeatures';
import { postUpdateShowHideRepository } from '../../../../repository/mytm/launchFeatures/launchFeaturesRepository';
import { API_URLS } from '../../../../constants/urls/api-urls';
import { btnActive } from '../../../../styles';
import { ConfirmAction } from '../../../molecules/confirm/ConfirmAction';
import { UserContext } from '../../../../repository/context/userContext';
import { postLoginRepository } from '../../../../repository/mytm/auth/loginRepository';
import { MESSAGE_ERROR } from '../../../../constants/message';
import { PAGES } from '../../../../constants/urls/page-urls';

interface ActionsColumnProps {
  country?: string;
  record: LaunchFeaturesType;
  onRefresh: () => void;
}

export default React.memo((props: ActionsColumnProps) => {
  const { t } = useTranslation(LAUNCH_FEATURES);
  const { country, record, onRefresh } = props;
  const userContext = useContext(UserContext);

  const [showHideLoading, setShowHideLoading] = useState(false);

  const availableCountry = record.countries.includes(country);
  const enabled = record.enables.includes(country);

  const location = useLocation();
  const currentPath = location.pathname;
  const hideFeatureGHIN = country !== 'USA' && record.feature === 'GHIN';
  let apiUrl: string = '';
  let isCountry: any;
  switch (currentPath) {
    case PAGES.PAID_FEATURES:
      apiUrl = API_URLS.PAID_FEATURES_SAVE;
      break;

    case PAGES.SHOW_HIDE_FEATURES:
      isCountry = true;
      apiUrl = API_URLS.LAUNCH_FEATURES_SAVE;
      break;

    default:
      apiUrl = API_URLS.LAUNCH_FEATURES_SAVE;
      break;
  }

  const onShowHideCard = () => {
    Modal.confirm({
      title: t('table.popup.title'),
      okText: t('table.popup.okBtn'),
      cancelText: t('table.popup.cancelBtn'),
      icon: <ExclamationCircleOutlined />,
      content: (
        <ConfirmAction
          textConfirmAction={
            <Trans components={{ bold: <strong /> }}>
              {record.enabled
                ? t('table.popup.disable', { feature: record.feature })
                : t('table.popup.enable', { feature: record.feature })}
            </Trans>
          }
          isConfirmPassword
          textConfirmPassword={t('table.popup.confirmPassword')}
        />
      ),
      onOk() {
        setShowHideLoading(true);
        const inputPasswordNode = document.getElementById('inputPasswordForConfirm');
        const password = (inputPasswordNode as any).value;
        const updateFunc = async () => {
          if (record.feature === 'SMSOTP' || record.feature === 'COUNTRY_SELECTION') {
            try {
              let arrPost: Array<any> = [];
              record.countries.forEach((item: string) => {
                const postAPI = postUpdateShowHideRepository(apiUrl, {
                  feature: record.feature,
                  enabled: !record?.enabled,
                  countryCode: item,
                  isCountry,
                });
                arrPost = [...arrPost, postAPI];
              });
              await Promise.all(arrPost);
              onRefresh();
              setShowHideLoading(false);

              return;
            } catch (error) {
              return;
            }
          }
          postUpdateShowHideRepository(apiUrl, {
            feature: record.feature,
            enabled: !record?.enables?.includes(country),
            countryCode: country,
            isCountry,
          })
            .then(r => {
              onRefresh();
            })
            .catch(e => {
              message.error(e?.errorMessage);
            })
            .finally(() => {
              setShowHideLoading(false);
            });
        };

        if (!userContext.user?.email) {
          setShowHideLoading(false);
          message.error(MESSAGE_ERROR.M02);
        } else {
          postLoginRepository({
            email: userContext.user?.email,
            password,
          })
            .then(() => {
              updateFunc();
            })
            .catch((e: any) => {
              setShowHideLoading(false);
              message.error(e?.errorMessage);
            });
        }
      },
    });
  };

  return (
    availableCountry && (
      <StyledContainer>
        <Space size="small">
          <Tooltip title={record.enabled ? t('table.tooltip.disable') : t('table.tooltip.enable')}>
            <Switch
              checked={enabled}
              onChange={onShowHideCard}
              disabled={hideFeatureGHIN || showHideLoading}
              loading={showHideLoading}
            />
          </Tooltip>
        </Space>
      </StyledContainer>
    )
  );
});

const StyledContainer = styled.div`
  ${btnActive}
`;
